'use strict'

/* -----------------------------------------------------------------------------
 * dependencies
 * -------------------------------------------------------------------------- */

// 3rd party
import React, { useEffect, useState, useCallback } from 'react'

// walrus
import Text from '@components/kit/text'
import Box from '@components/kit/box'
import { Column, Row } from '@components/kit/grid'
import Loading from '@components/kit/loading'
import FlatList from '@components/kit/flat-list'
import { BoxLink } from '@components/kit/link'

// local
import { IOpportunity } from '@store/Opportunity/types'
import { useOpportunityStore } from '@store/Opportunity'

/* -----------------------------------------------------------------------------
 * Constants
 * -------------------------------------------------------------------------- */

const PAGE_SIZE = 10

/* -----------------------------------------------------------------------------
 * MemberListLoading
 * -------------------------------------------------------------------------- */

const MemberListLoading = () => {
  return (
    <Box flex='1 1 auto' alignItems='center' justifyContent='center' py='40'>
      <Loading />
    </Box>
  )
}

/* -----------------------------------------------------------------------------
 * MemberHeader
 * -------------------------------------------------------------------------- */

export function MemberHeader() {
  return (
    <Row
      borderTopLeftRadius='medium'
      borderTopRightRadius='medium'
      backgroundColor='white'
      borderColor='shade02'
      borderStyle='solid'
      borderRightWidth={1}
      borderBottomWidth={1}
      maxHeight={40}
    >
      <Column width={110} paddingX='16' paddingY='04' justifyContent='center'>
        <Text fontSize={12} textColor='purple' fontWeight='bold'>
          MEMBER ID
        </Text>
      </Column>
      <Column
        width={215}
        paddingY='04'
        borderColor='shade02'
        borderStyle='solid'
        borderLeftWidth={1}
        justifyContent='center'
      >
        <Text fontSize={12} textColor='purple' fontWeight='bold'>
          OPPORTUNITY TYPE
        </Text>
      </Column>
      <Column
        width={110}
        paddingX='16'
        paddingY='04'
        borderColor='shade02'
        borderStyle='solid'
        borderLeftWidth={1}
        justifyContent='center'
      >
        <Text fontSize={12} textColor='purple' fontWeight='bold'>
          CURRENT GPI
        </Text>
      </Column>
      <Column
        width={110}
        paddingX='16'
        paddingY='04'
        borderColor='shade02'
        borderStyle='solid'
        borderLeftWidth={1}
        justifyContent='center'
      >
        <Text fontSize={12} textColor='purple' fontWeight='bold'>
          TARGET GPI 1
        </Text>
      </Column>
      {/* <Column
        width={110}
        paddingX='16'
        paddingY='04'
        borderColor='shade02'
        borderStyle='solid'
        borderLeftWidth={1}
        justifyContent='center'
      >
        <Text fontSize={12} textColor='purple' fontWeight='bold'>
          YEAR SAVINGS
        </Text>
      </Column> */}
      <Column
        width={215}
        paddingX='16'
        paddingY='04'
        borderColor='shade02'
        borderStyle='solid'
        borderLeftWidth={1}
        justifyContent='center'
      >
        <Text fontSize={12} textColor='purple' fontWeight='bold'>
          CLIENT
        </Text>
      </Column>
    </Row>
  )
}

/* -----------------------------------------------------------------------------
 * MemberRow
 * -------------------------------------------------------------------------- */

export type MemberRowProps = {
  opportunity: IOpportunity
  categoryId: number
}

export function MemberRow({ opportunity, categoryId }: MemberRowProps) {
  const { id, user_id, opportunity_type, client, year_savings } = opportunity

  return (
    <Row borderColor='shade02' borderStyle='solid' borderBottomWidth={1}>
      <Column width={110} paddingX='16' paddingY='04'>
        <BoxLink path={`/chat/${user_id}`}>
          <Text fontSize={13} textColor='violet' numberOfLines={1}>
            {user_id}
          </Text>
        </BoxLink>
      </Column>
      <Column
        width={215}
        paddingX='16'
        paddingY='04'
        borderColor='shade02'
        borderStyle='solid'
        borderLeftWidth={1}
      >
        <BoxLink
          path={`/opportunities/category/${categoryId}/opportunity/${id}`}
        >
          <Text fontSize={13} textColor='violet'>
            {opportunity_type || 'N/A'}
          </Text>
        </BoxLink>
      </Column>
      <Column
        width={110}
        paddingX='16'
        paddingY='04'
        borderColor='shade02'
        borderStyle='solid'
        borderLeftWidth={1}
      >
        <Text fontSize={13} textColor='purple'>
          {opportunity.current_gpi || 'N/A'}
        </Text>
      </Column>
      <Column
        width={110}
        paddingX='16'
        paddingY='04'
        borderColor='shade02'
        borderStyle='solid'
        borderLeftWidth={1}
      >
        <Text fontSize={13} textColor='purple'>
          {opportunity.target_gpi_1 || 'N/A'}
        </Text>
      </Column>
      {/* <Column
        width={110}
        paddingX='16'
        paddingY='04'
        borderColor='shade02'
        borderStyle='solid'
        borderLeftWidth={1}
      >
        <Text fontSize={13} textColor='purple'>
          {year_savings || 'N/A'}
        </Text>
      </Column> */}
      <Column
        width={215}
        paddingX='16'
        paddingY='04'
        borderColor='shade02'
        borderStyle='solid'
        borderLeftWidth={1}
      >
        <Text fontSize={13} textColor='purple'>
          {client || 'N/A'}
        </Text>
      </Column>
    </Row>
  )
}

/* -----------------------------------------------------------------------------
 * MemberList
 * -------------------------------------------------------------------------- */

type MemberListProps = {
  data: IOpportunity[]
  fetchNextPage: () => void
  isFetchingNextPage: boolean
  categoryId: number
}

export function MemberList({
  data,
  fetchNextPage,
  isFetchingNextPage,
  categoryId
}: MemberListProps) {
  if (!data || !data.length) {
    return (
      <Box flex='1 1 auto' alignItems='center' justifyContent='center' py='40'>
        <Text fontSize={13} textColor='purple'>
          No matching members found
        </Text>
      </Box>
    )
  }
  return (
    <FlatList
      data={data}
      keyExtractor={(item: IOpportunity) => item.id.toString()}
      renderItem={({ item }) => (
        <MemberRow opportunity={item as IOpportunity} categoryId={categoryId} />
      )}
      initialNumToRender={PAGE_SIZE}
      onEndReached={fetchNextPage}
      ListFooterComponent={isFetchingNextPage && <MemberListLoading />}
      onEndReachedThreshold={0.5}
    />
  )
}

/* -----------------------------------------------------------------------------
 * MatchingMembers
 * -------------------------------------------------------------------------- */

type MatchingMembersProps = {
  categoryId: number
}

export default function MatchingMembers({ categoryId }: MatchingMembersProps) {
  const [data, setData] = useState<IOpportunity[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [currentPage, setCurrentPage] = useState<number>(0)
  const [hasNextPage, setHasNextPage] = useState<boolean>(true)
  const { getOpportunitiesFromCategories } = useOpportunityStore.getState()

  const fetchOpportunities = useCallback(async () => {
    if (!hasNextPage || isLoading) return

    setIsLoading(true)
    const fetchedOpportunities = await getOpportunitiesFromCategories(
      categoryId,
      currentPage,
      PAGE_SIZE
    )
    if (fetchedOpportunities) {
      setData(prevData => [...prevData, ...fetchedOpportunities.results])
      setHasNextPage(fetchedOpportunities.has_next_page)
      setCurrentPage(prevPage => prevPage + 1)
    }
    setTimeout(() => {
      setIsLoading(false)
    }, 1000)
  }, [
    categoryId,
    currentPage,
    hasNextPage,
    isLoading,
    getOpportunitiesFromCategories
  ])

  useEffect(() => {
    if (!data.length && !isLoading) {
      fetchOpportunities()
    }
  }, [data.length, isLoading, fetchOpportunities])

  return (
    <Box maxWidth={980} mt='16' height='calc(100vh - 218px)'>
      <Text variant='h3' mb='16'>
        Matching Members
      </Text>

      <Box bg='white' borderRadius='medium' maxHeight='calc(100% - 40px)'>
        <MemberHeader />
        {isLoading && currentPage === 0 ? (
          <MemberListLoading />
        ) : (
          <MemberList
            data={data ?? []}
            fetchNextPage={fetchOpportunities}
            isFetchingNextPage={isLoading && hasNextPage}
            categoryId={categoryId}
          />
        )}
      </Box>
    </Box>
  )
}
